import { imageDataToBase64 } from '@/common/images';
import { Camera } from '@/components/camera/component';

import { MobileHeader } from '@/components/workflow/mobile-header';
import { useBaleInboundContext } from '../hook';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Page for capturing multiple photos of the outbound load.
 *
 * This component provides a camera interface for taking multiple photos
 * of the bale load before it leaves the facility. These photos serve as
 * documentation for the load's condition and contents.
 *
 * Features:
 * - Multi-photo capture capability
 * - High-quality JPEG compression (80%) for documentation
 * - Automatic navigation back to root page after photos are taken
 * - Photos are appended to existing photos in form data
 */
export const BaleInboundAddLoadPhotosPage = () => {
	// ========================================================================
	// CONTEXT
	// ========================================================================

	const { setPage, handleManualCreateInput, formData, workflowType } = useBaleInboundContext();

	// ========================================================================
	// EVENT HANDLERS
	// ========================================================================

	/**
	 * Handle multiple photo capture and processing
	 *
	 * Converts all captured image data to base64 format and appends
	 * them to the existing photos in the form data.
	 *
	 * @param imageData - Array of captured image data from the camera
	 */
	const handleAddPhoto = (imageData: ImageData[]) => {
		// Convert all images to base64 data URLs with high quality
		const photoDataUrls = imageData.map((image) => {
			return `data:image/jpeg;base64,${imageDataToBase64(image, 'jpeg', 0.8)}`;
		});

		// Append new photos to existing photos array
		handleManualCreateInput('loadPhotos', [...formData.loadPhotos, ...photoDataUrls]);

		// Navigate back to the root page
		setPage(workflowType === 'MANUAL' ? 'MANUAL_CREATE:ROOT' : 'EVALUATION');
	};

	/**
	 * Handle back navigation to root page
	 */
	const handleBack = () => {
		setPage(workflowType === 'MANUAL' ? 'MANUAL_CREATE:ROOT' : 'EVALUATION');
	};

	// ========================================================================
	// RENDER
	// ========================================================================

	return (
		<div>
			<MobileHeader title="Add Load Photos" onBack={handleBack} />
			<Camera multiple onTakePhoto={handleAddPhoto} />
		</div>
	);
};
